import { useEffect, useState } from "react";
import { Heart, Baby, Sparkles } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import type { EventTypeChoice } from "@/lib/event-templates";

export interface EventTypeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Fired once the customer confirms a type; the caller then reveals the create form. */
  onSelect: (choice: EventTypeChoice) => void;
}

const DEFAULT_CHOICE: EventTypeChoice = "wedding";

const OPTIONS: { value: EventTypeChoice; icon: typeof Heart }[] = [
  { value: "wedding", icon: Heart },
  { value: "baptism", icon: Baby },
  { value: "both", icon: Sparkles },
];

/**
 * JUB-36: the first step of event creation — pick wedding / baptism / both
 * before the rest of the form appears. The choice drives the form's required
 * fields (couple names vs. baby names) and the default template
 * (getDefaultTemplateForEventType), so the customer is never asked to pick a
 * template on top of a type. Changing the type afterwards still happens in
 * the editor's General tab (EventTypeSelector) — this modal only gates the
 * first save, it is not a one-way door.
 */
export function EventTypeModal({ open, onOpenChange, onSelect }: EventTypeModalProps) {
  const { t } = useTranslation();
  const [choice, setChoice] = useState<EventTypeChoice>(DEFAULT_CHOICE);

  // Reset to the default every time the modal is (re)opened, so a cancelled
  // pick from a previous open doesn't linger.
  useEffect(() => {
    if (open) setChoice(DEFAULT_CHOICE);
  }, [open]);

  const handleContinue = () => {
    onSelect(choice);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="event-type-modal">
        <DialogClose onClose={() => onOpenChange(false)} />
        <DialogHeader>
          <DialogTitle>{t("events.form.typeModalTitle")}</DialogTitle>
          <DialogDescription>{t("events.form.typeModalDescription")}</DialogDescription>
        </DialogHeader>

        <RadioGroup value={choice} onValueChange={(value) => setChoice(value as EventTypeChoice)} className="gap-3 py-2">
          {OPTIONS.map(({ value, icon: Icon }) => {
            const inputId = `event-type-modal-${value}`;
            const isActive = choice === value;
            return (
              <label
                key={value}
                htmlFor={inputId}
                className={cn(
                  "flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors",
                  isActive ? "border-primary bg-primary/5" : "border-input hover:bg-muted/50"
                )}
              >
                <RadioGroupItem
                  id={inputId}
                  name="event-type-modal-choice"
                  value={value}
                  className="mt-1"
                  data-testid={`event-type-modal-option-${value}`}
                />
                <span className="flex items-start gap-2">
                  <Icon className="mt-0.5 h-4 w-4 shrink-0 text-pink-600 dark:text-pink-400" aria-hidden="true" />
                  <span>
                    <span className="block font-medium">{t(`events.form.typeModal.${value}.label`)}</span>
                    <span className="block text-sm text-muted-foreground">
                      {t(`events.form.typeModal.${value}.description`)}
                    </span>
                  </span>
                </span>
              </label>
            );
          })}
        </RadioGroup>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            type="button"
            onClick={handleContinue}
            className="bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white"
            data-testid="event-type-modal-continue"
          >
            {t("events.form.typeModalContinue")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
